import { useEffect, useState } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { LineSkeleton } from './ToolGrid';

const KINDS = [
  { kind: 'Component', label: 'Components', to: '/catalog?filters[kind]=component' },
  { kind: 'API', label: 'APIs', to: '/catalog?filters[kind]=api' },
  { kind: 'System', label: 'Systems', to: '/catalog?filters[kind]=system' },
  { kind: 'Resource', label: 'Resources', to: '/catalog?filters[kind]=resource' },
];

const useStyles = makeStyles({
  // Divided rows rather than nested cards — the panel is already a surface.
  rows: { display: 'grid', gap: 0 },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    padding: '13px 0',
    borderBottom: '1px solid var(--q-border)',
    textDecoration: 'none',
    '&:last-child': { borderBottom: 'none' },
    '&:hover $count': { color: 'var(--q-emerald-mint)' },
  },
  label: {
    fontFamily: 'var(--q-font-mono)',
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--q-muted)',
  },
  count: {
    fontFamily: 'var(--q-font-display)',
    fontWeight: 700,
    fontSize: 26,
    letterSpacing: '-0.02em',
    color: 'var(--q-text)',
    transition: 'color 200ms var(--q-ease)',
    fontVariantNumeric: 'tabular-nums',
  },
  empty: {
    color: 'var(--q-sub)',
    fontSize: 13.5,
    lineHeight: 1.6,
    margin: 0,
  },
  error: {
    color: 'var(--q-orange)',
    fontFamily: 'var(--q-font-mono)',
    fontSize: 11.5,
    letterSpacing: '0.04em',
    margin: 0,
  },
});

type State =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; counts: Record<string, number> };

export const CatalogPulse = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<State>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const entries = await Promise.all(
          KINDS.map(async k => {
            const res = await catalogApi.getEntities({
              filter: { kind: k.kind },
              // Only the ref is needed to count; keeps the payload small.
              fields: ['kind'],
            });
            return [k.kind, res.items.length] as const;
          }),
        );
        if (!cancelled) {
          setState({ phase: 'ready', counts: Object.fromEntries(entries) });
        }
      } catch (e) {
        if (!cancelled) {
          setState({
            phase: 'error',
            message: e instanceof Error ? e.message : 'Catalog unavailable',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  if (state.phase === 'loading') {
    return (
      <div className={classes.rows}>
        {KINDS.map(k => (
          <div key={k.kind} className={classes.row}>
            <span className={classes.label}>{k.label}</span>
            <LineSkeleton width="46px" />
          </div>
        ))}
      </div>
    );
  }

  if (state.phase === 'error') {
    return <p className={classes.error}>Catalog unreachable — {state.message}</p>;
  }

  const total = Object.values(state.counts).reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <p className={classes.empty}>
        Nothing catalogued yet. Register your first service from{' '}
        <Link to="/catalog-import">catalog import</Link>, or scaffold one with a{' '}
        <Link to="/create">template</Link>.
      </p>
    );
  }

  return (
    <div className={classes.rows}>
      {KINDS.map(k => (
        <Link key={k.kind} to={k.to} underline="none" className={classes.row}>
          <span className={classes.label}>{k.label}</span>
          <span className={classes.count}>{state.counts[k.kind] ?? 0}</span>
        </Link>
      ))}
    </div>
  );
};
